import React, { useCallback, useEffect } from 'react'
import ScreenWrapper from '~/components/screen_wrappers/screen_wrapper'
import { useClaims } from '@verzekeringapp-mono/shared/src/hooks/use_claims'
import {
  useAppDispatch,
  useAppSelector
} from '@verzekeringapp-mono/shared/src/hooks/state'
import { getClaim as getClaimSelector } from '@verzekeringapp-mono/shared/src/state/selectors/claims'
import { useNavigate, useParams } from 'react-router-dom'
import { getToken } from '@verzekeringapp-mono/shared/src/state/selectors/user'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  setClaimDamageForm,
  setConceptClaim,
  setCurrentStep
} from '@verzekeringapp-mono/shared/src/state/slices/claims_slice'
import { setSelectedPolicyNumber } from '@verzekeringapp-mono/shared/src/state/slices/policies_slice'
import { format } from 'date-fns'
import { useThemeColorContext } from '~/providers/ThemeColorProvider'
import { useSecureFile } from '~/hooks/use_secure_file'
import Modal from '~/components/Modal'
import LoadingIndicator from '~/components/LoadingIndicator'
import { useMemo } from 'react'
import { resolveClaimStatusColor } from '@verzekeringapp-mono/shared/src/utils/claim_status_color'

const ClaimDetailScreen: React.FC = () => {
  const { id } = useParams()
  const { getClaim, deleteClaim } = useClaims()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const claim = useAppSelector(getClaimSelector(id ?? ''))
  const { themeColors, themeMode } = useThemeColorContext()
  const token = useAppSelector(getToken)
  const { openFile, loading } = useSecureFile()
  const statusColor = useMemo(
    () => claim
      ? resolveClaimStatusColor(claim.status, claim.statusColor, themeColors, themeMode)
      : themeColors.text.secondary,
    [claim, themeColors, themeMode]
  )

  useEffect(() => {
    if (claim) {
      getClaim(claim.id || '')
    }
  }, [])

  const onPressRemove = useCallback(async () => {
    const confirmed = window.confirm(
      'Weet je zeker dat je de concept schademelding wilt verwijderen?'
    )

    if (confirmed) {
      const success = await deleteClaim(claim?.id || '')

      if (success) {
        navigate('/dossier/mijn-schades')
      }
    }
  }, [claim, deleteClaim])

  if (!claim) {
    // TODO: Should show 404 page
    return null
  }

  return (
    <ScreenWrapper
      breadCrumbsOverride={[
        { label: 'Dossier', path: '/dossier' },
        { label: 'Mijn schades', path: '/dossier/mijn-schades' },
        { label: 'Claim', path: '/claims' }
      ]}
      hasPadding={false}
    >
      <div className='flex h-full flex-col gap-4 rounded border border-border-subtle bg-surface-card p-8'>
        <div className='flex justify-between'>
          <div>
            <p className='text-lg'>Polis</p>

            <p className='light'>{claim?.productName}</p>
          </div>

          <div className='flex gap-2'>
            <div
              className='flex items-center justify-center px-4 rounded-full'
              style={{ backgroundColor: statusColor }}
            >
              <p className='paragraph-light text-center text-text-on-neutral-strong'>{claim?.status}</p>
            </div>

            {claim?.status === 'Concept' && (
              <button
                type='button'
                onClick={onPressRemove}
                className='text-status-danger-foreground'
              >
                <FontAwesomeIcon
                  icon='trash'
                />
              </button>
            )}
          </div>
        </div>

        <div className='flex flex-col gap-1'>
          <p>Datum waarop de schade is ontstaan</p>

          <p className='text-sm light'>
            {claim?.dateOfLoss
              ? format(new Date(claim.dateOfLoss), 'dd-MM-yyyy')
              : '-'}
          </p>
        </div>

        <div className='flex flex-col gap-1'>
          <p>Je omschrijving van de schade</p>

          <p className='paragraph-light'>{claim?.text ?? '-'}</p>
        </div>

        <div className='flex flex-col gap-1'>
          <p>Je telefoonnummer</p>

          <p className='paragraph-light'>{claim.phoneNumber ?? '-'}</p>
        </div>

        <div className='flex flex-col gap-1'>
          <p>Bijlage(n)</p>

          {
            (!claim?.files || claim.files.length === 0) && (
              <p className='paragraph-light'>-</p>
            )
          }

          {claim?.files?.map(file => (
            <button
              type='button'
              key={file.url}
              onClick={async () => {
                try {
                  await openFile(file.url, {
                    extension: file.extension,
                    filename: file.title,
                    token: token?.accessToken || ''
                  })
                } catch (error) {
                  alert(
                    'Er is een fout opgetreden bij het ophalen van het bestand. Probeer het later opnieuw.'
                  )
                }
              }}
              className='text-left paragraph-light underline'
            >
              {`- ${file.title}`}
            </button>
          ))}
        </div>

        <div className='flex flex-col gap-1'>
          <p>Schademelddatum</p>

          <p className='paragraph-light'>
            {claim?.lossNotificationDate
              ? format(new Date(claim.lossNotificationDate), 'dd-MM-yyyy')
              : '-'}
          </p>
        </div>

        {claim?.status === 'Concept' && (
          <button
            type='button'
            onClick={() => {
              dispatch(setCurrentStep(1))
              dispatch(setConceptClaim(claim))
              dispatch(setSelectedPolicyNumber(claim.policyNumber))
              dispatch(
                setClaimDamageForm({
                  policyId: claim.policyNumber,
                  date: claim.dateOfLoss,
                  description: claim.text,
                  phoneNumber: claim.phoneNumber,
                  attachments: claim.files?.map(file => ({
                    title: file.title,
                    url: file.url,
                    size: file.size,
                    extension: file.extension
                  }))
                })
              )

              navigate('/schade-melden/schadeformulier')
            }}
            className='primary-btn w-full md:w-1/2'
          >
            Schademelding afronden
          </button>
        )}

        {loading && (
          <Modal>
            <div className='p-8 flex flex-col justify-center items-center gap-4 md:h-2/4 h-full'>
              <p>Bestand wordt opgehaald</p>

              <LoadingIndicator />
            </div>
          </Modal>
        )}
      </div>
    </ScreenWrapper>
  )
}

export default ClaimDetailScreen
